/**
 * Fetching the normalized manifest.
 *
 * In production these paths are the Worker reading from R2; in `npm run dev` a
 * Vite middleware serves the same shapes out of `manifest-dist/`. Either way the
 * app only ever sees the slim normalized model.
 */
import type { CraftableWeapon, CraftablesIndex } from "@/lib/crafting/types";

export type LoadResult<T> =
  | { status: "ok"; data: T }
  | { status: "unavailable"; message: string; hint?: string }
  | { status: "error"; message: string };

async function loadJson<T>(url: string, notFoundMessage: string): Promise<LoadResult<T>> {
  let res: Response;
  try {
    res = await fetch(url, { headers: { Accept: "application/json" } });
  } catch {
    return {
      status: "error",
      message: "Could not reach the server. Check your connection and try again.",
    };
  }

  if (res.status === 404) return { status: "error", message: notFoundMessage };

  if (!res.ok) {
    // 503 is the Worker/dev-server saying the bundle has not been published.
    const body = (await res.json().catch(() => null)) as
      | { error?: string; hint?: string }
      | null;
    if (res.status === 503) {
      return {
        status: "unavailable",
        message: body?.error ?? "Manifest data is not available yet.",
        hint: body?.hint ?? "Run `npm run sync:manifest` to build it.",
      };
    }
    return { status: "error", message: body?.error ?? `Request failed (${res.status}).` };
  }

  try {
    return { status: "ok", data: (await res.json()) as T };
  } catch {
    return { status: "error", message: "The manifest data was malformed." };
  }
}

export function fetchCatalogue(): Promise<LoadResult<CraftablesIndex>> {
  return loadJson<CraftablesIndex>("/api/manifest/index", "The weapon catalogue is missing.");
}

export function fetchWeapon(recipeHash: number): Promise<LoadResult<CraftableWeapon>> {
  return loadJson<CraftableWeapon>(
    `/api/manifest/weapon/${recipeHash}`,
    "That weapon is not in the catalogue. It may have been removed in a game update."
  );
}

// --- session -----------------------------------------------------------------

export interface SessionInfo {
  signedIn: boolean;
  displayName?: string | null;
}

/** Never throws: not being signed in is the normal case, not an error. */
export async function fetchSession(): Promise<SessionInfo> {
  try {
    const res = await fetch("/api/me", { headers: { Accept: "application/json" } });
    if (!res.ok) return { signedIn: false };
    return (await res.json()) as SessionInfo;
  } catch {
    return { signedIn: false };
  }
}

// --- crafted weapons ---------------------------------------------------------

export interface CraftedWeaponInstance {
  itemInstanceId: string;
  itemHash: number;
  location: string;
  sockets: number[];
  weaponLevel?: number;
}

/** The signed-in account's crafted weapons. 401 when signed out. */
export async function fetchCraftedWeapons(): Promise<LoadResult<CraftedWeaponInstance[]>> {
  const result = await loadJson<{ weapons: CraftedWeaponInstance[] }>(
    "/api/me/crafted",
    "Could not read your crafted weapons."
  );
  if (result.status !== "ok") return result;
  return { status: "ok", data: result.data.weapons ?? [] };
}

// --- shared plans ------------------------------------------------------------

export interface SharedPlanResponse {
  plan: unknown;
  sharedBy: string | null;
  createdAt: number;
}

export function fetchSharedPlan(id: string): Promise<LoadResult<SharedPlanResponse>> {
  return loadJson<SharedPlanResponse>(
    `/api/plans/${id}`,
    "That shared plan no longer exists."
  );
}

export type CreateShareResult =
  | { status: "ok"; url: string }
  | { status: "unauthenticated" }
  | { status: "error"; message: string };

/**
 * Store a plan so its link carries the author's name. Returns `unauthenticated`
 * rather than an error when signed out — the caller falls back to a stateless
 * link, which works fine, just without attribution.
 */
export async function createSharedPlan(plan: unknown): Promise<CreateShareResult> {
  let res: Response;
  try {
    res = await fetch("/api/plans", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(plan),
    });
  } catch {
    return { status: "error", message: "Could not reach the server." };
  }

  if (res.status === 401) return { status: "unauthenticated" };
  if (!res.ok) {
    const body = (await res.json().catch(() => null)) as { error?: string } | null;
    return { status: "error", message: body?.error ?? `Could not share the plan (${res.status}).` };
  }

  const body = (await res.json()) as { url?: string };
  if (!body.url) return { status: "error", message: "The server did not return a link." };
  return { status: "ok", url: body.url };
}
